import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CONTACT } from "../lib/constants";

const STATS = [
  { k: "15+", v: "Years of Ahmedabad West intelligence" },
  { k: "5", v: "Asset classes under one mandate" },
  { k: "12", v: "Core micro-markets we track weekly" },
  { k: "1:1", v: "Conversations, always with the proprietor" },
];

const PILLARS = [
  {
    n: "01",
    t: "Consultation first",
    b: "We'd rather spend an hour understanding your end-use, family profile and timeline than send you property links all afternoon. It sounds slow. It is, by far, the fastest path to a good deal.",
  },
  {
    n: "02",
    t: "Small, focused, independent",
    b: "Vachhraj Estate is a sole-proprietor practice. No hand-offs, no quota pressure, no misaligned incentives. The person you meet is the person who closes your transaction.",
  },
  {
    n: "03",
    t: "Cross-asset perspective",
    b: "Most brokers specialise in one vertical. We work across commercial, residential, industrial, farmhouses and plotting — which means we notice arbitrage and rebalancing opportunities others miss.",
  },
  {
    n: "04",
    t: "Quiet on details",
    b: "Most of our clients are business families, senior executives and NRIs. Discretion is assumed. Your enquiry, your budget and your decision stay with us.",
  },
];

const AboutPage = () => {
  return (
    <div>
      <section className="relative ve-noise">
        <div className="ve-container pt-20 md:pt-28 pb-16">
          <span className="ve-eyebrow">About the Practice</span>
          <h1 className="ve-display mt-6 text-[2.6rem] sm:text-4xl md:text-6xl lg:text-7xl max-w-5xl">
            Built the old-fashioned way —<br />
            <em>one relationship at a time.</em>
          </h1>
          <p className="mt-8 max-w-3xl text-base md:text-lg leading-relaxed" style={{ color: "var(--cream-200)" }}>
            Vachhraj Estate is the personal real estate consulting practice of a single proprietor who has worked the Ahmedabad West market for more than a decade. It exists because the city's fast-moving western corridor — Prahlad Nagar, Satellite, Bodakdev, Sindhu Bhavan, Shela and the emerging Iscon Ambli / Science City belts — rewards people who track it street by street, every single day. Not once a quarter. Not from a spreadsheet.
          </p>
        </div>
        <div className="ve-rule" />
        <div className="ve-container py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <div key={i}>
              <div className="text-5xl md:text-6xl" style={{ fontFamily: "Fraunces, serif", color: "var(--terra-500)", fontWeight: 300 }}>
                {s.k}
              </div>
              <div className="mt-3 text-xs tracking-[0.18em] uppercase" style={{ color: "var(--cream-300)" }}>
                {s.v}
              </div>
            </div>
          ))}
        </div>
        <div className="ve-rule" />
      </section>

      <section className="ve-container py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="ve-eyebrow">How We Work</span>
            <h2 className="ve-display mt-5 text-4xl md:text-5xl">
              Four<br />
              <em>non-negotiables.</em>
            </h2>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            {PILLARS.map((p, i) => (
              <div key={i} className="ve-card">
                <div className="ve-number">{p.n}</div>
                <h3 className="mt-3 text-2xl">{p.t}</h3>
                <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--cream-200)" }}>
                  {p.b}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-b" style={{ borderColor: "var(--hair)", background: "var(--ink-900)" }}>
        <div className="ve-container py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="ve-eyebrow">Philosophy</span>
            <h2 className="ve-display mt-5 text-4xl md:text-5xl">
              Real estate is<br />
              <em>a long sentence.</em>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--cream-200)" }}>
            <p>
              A home is not a transaction. Neither is a showroom, a shed or a plot. Each of them is the opening line of a story that will run for ten, twenty, sometimes forty years. A lazy decision on day one becomes a quiet tax every month afterwards.
            </p>
            <p>
              Our job is to make sure the first line is a good one. We read blueprints, walk buildings after 8 PM to check lifts and water pressure, pull RERA filings, talk to existing occupants and, most importantly, ask the uncomfortable questions that family friends rarely do.
            </p>
            <p style={{ color: "var(--gold-400)", fontStyle: "italic", fontFamily: "Fraunces, serif" }}>
              If we don't think it's right for you — even if it's commission-positive for us — we'll say so and move on. That's the entire business model.
            </p>
          </div>
        </div>
      </section>

      <section className="ve-container py-24">
        <div className="max-w-4xl">
          <h2 className="ve-display text-4xl md:text-6xl">
            Come visit the<br />
            <em>Prahlad Nagar office.</em>
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed max-w-2xl" style={{ color: "var(--cream-200)" }}>
            The best way to evaluate a consultant is in person. Drop in during working hours and we'll walk you through the market over a chai — no commitment, no obligation.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact" className="ve-btn-primary">
              Plan Your Visit <ArrowUpRight size={14} />
            </Link>
            <a href={CONTACT.whatsappLink} target="_blank" rel="noreferrer" className="ve-btn-whatsapp">
              Message on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;